import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getWishlists, removeFromWishlist } from "../../services/wishlist";
import { addToCart } from "../../services/cart";
import { useCart } from "../../contexts/CartContext";
import { toast } from "react-toastify";
import logoImage from "../../assets/images/logo.png";
import "../../styles/Wishlist.css";

// ── Icons ────────────────────────────────────────────
const TrashIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const CartIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </svg>
);

const TagIcon = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

const StoreIcon = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const Spinner = ({ size = 14, color = "#fff" }) => (
  <span
    style={{
      display: "inline-block",
      width: size,
      height: size,
      border: `2px solid ${color}33`,
      borderTopColor: color,
      borderRadius: "50%",
      animation: "wl-spin 0.65s linear infinite",
      flexShrink: 0,
    }}
  />
);

// ── Helpers ──────────────────────────────────────────
const normalizeHttps = (url) =>
  typeof url === "string" ? url.replace("http://", "https://") : url;

const getFirstValue = (value) => {
  if (Array.isArray(value)) return value[0] || null;
  return value || null;
};

const resolvePriceData = (product = {}) => {
  const regularPrice = Number(product.price ?? 0);
  const discountPrice = Number(product.product_discount_price ?? 0);
  const flashSalePrice = Number(product.flash_sale_price ?? 0);
  const apiFinalPrice = Number(product.final_price ?? 0);

  let finalPrice = regularPrice;
  if (apiFinalPrice > 0) finalPrice = apiFinalPrice;
  else if (flashSalePrice > 0) finalPrice = flashSalePrice;
  else if (discountPrice > 0) finalPrice = discountPrice;

  const hasDiscount =
    regularPrice > 0 && finalPrice > 0 && finalPrice < regularPrice;

  const discountPercent = hasDiscount
    ? Math.round(((regularPrice - finalPrice) / regularPrice) * 100)
    : 0;

  const pricingType = product.pricing_type || "regular";
  const isFlashSale = Boolean(product.is_flash_sale) || pricingType === "flash_sale";

  return {
    regularPrice,
    discountPrice,
    flashSalePrice,
    finalPrice,
    pricingType,
    isFlashSale,
    hasDiscount,
    discountPercent,
  };
};

// ── Component ────────────────────────────────────────
export default function Wishlist() {
  const navigate = useNavigate();
  const { updateCartCount } = useCart();

  const [wishlistData, setWishlistData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [removing, setRemoving] = useState({});
  const [addingToCart, setAddingToCart] = useState({});

  const fetchWishlists = async () => {
    try {
      setLoading(true);
      setError("");

      const [response] = await Promise.all([
        getWishlists(),
        new Promise((r) => setTimeout(r, 400)),
      ]);

      const arr = response?.data || response || [];
      setWishlistData(Array.isArray(arr) ? arr : []);
    } catch {
      setError("Error loading wishlist. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWishlists();
  }, []);

  const handleAddToCart = async (item) => {
    const productId = item.product?.id || item.product_id;
    if (!productId) return toast.error("Product information is missing");

    try {
      setAddingToCart((p) => ({ ...p, [item.id]: true }));

      await addToCart({
        productId,
        quantity: 1,
        color: getFirstValue(item.product?.color),
        size: getFirstValue(item.product?.size),
      });

      updateCartCount();
      toast.success("Added to cart!");
    } catch {
      toast.error("Failed to add to cart.");
    } finally {
      setAddingToCart((p) => ({ ...p, [item.id]: false }));
    }
  };

  const handleRemove = async (wishlistId) => {
    try {
      setRemoving((p) => ({ ...p, [wishlistId]: true }));
      await removeFromWishlist(wishlistId);
      setWishlistData((p) => p.filter((x) => x.id !== wishlistId));
      toast.success("Removed from wishlist!");
    } catch {
      toast.error("Failed to remove item.");
    } finally {
      setRemoving((p) => ({ ...p, [wishlistId]: false }));
    }
  };

  const openProduct = (product) => {
    if (!product) return;
    if (product.slug) return navigate(`/product/${product.slug}`);
    if (product.id) return navigate(`/product/${product.id}`);
  };

  return (
    <div className="wl-root">
      <div className="acc-head">
        <div>
          <h1 className="acc-h1">My Wishlist</h1>
          {!loading && wishlistData.length > 0 && (
            <p className="wl-count">
              {wishlistData.length} saved item{wishlistData.length !== 1 ? "s" : ""}
            </p>
          )}
        </div>

        <button className="acc-btn" onClick={fetchWishlists} disabled={loading}>
          {loading ? (
            <>
              <Spinner size={12} />
              &nbsp;Refreshing…
            </>
          ) : (
            "Refresh"
          )}
        </button>
      </div>

      {loading ? (
        <div className="acc-box wl-loading-box">
          <Spinner size={28} color="#E67E42" />
          <span>Loading your wishlist…</span>
        </div>
      ) : error ? (
        <div className="acc-error">{error}</div>
      ) : wishlistData.length === 0 ? (
        <div className="acc-box wl-empty-box">
          <div className="wl-empty-icon">🤍</div>
          <div className="wl-empty-title">Your wishlist is empty</div>
          <div className="wl-empty-sub">
            Save items you love and find them here anytime.
          </div>
          <button className="acc-btn" onClick={() => navigate("/explore")}>
            Browse Products
          </button>
        </div>
      ) : (
        <div className="wl-list">
          {wishlistData.map((item, i) => {
            const product = item.product || {};

            const img = normalizeHttps(
              product.product_images?.[0] ||
                product.image_url ||
                item.product_image ||
                item.image ||
                logoImage
            );

            const name =
              product.name || item.product_name || item.name || "Product";

            const brand =
              product.brand || item.brand || item.product_brand || null;

            const shop =
              product.vendor?.store_name ||
              item.shop_name ||
              item.store_name ||
              null;

            const {
              regularPrice,
              finalPrice,
              pricingType,
              isFlashSale,
              hasDiscount,
              discountPercent,
            } = resolvePriceData(product);

            const isRemoving = !!removing[item.id];
            const isAddingCart = !!addingToCart[item.id];

            const showBadge = hasDiscount && discountPercent > 0;
            const badgeText =
              pricingType === "flash_sale" || isFlashSale
                ? `FLASH ${discountPercent}% OFF`
                : `${discountPercent}% OFF`;

            return (
              <div
                key={item.id}
                className="wl-card"
                style={{ animationDelay: `${i * 45}ms` }}
              >
                <div
                  className="wl-card-img"
                  onClick={() => openProduct(product)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openProduct(product);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  title="View product"
                >
                  <img
                    src={img}
                    alt={name}
                    onError={(e) => {
                      e.target.src = logoImage;
                    }}
                  />

                  {showBadge && (
                    <span
                      className={`wl-discount-badge ${
                        pricingType === "flash_sale" || isFlashSale ? "flash-sale" : ""
                      }`}
                    >
                      {badgeText}
                    </span>
                  )}
                </div>

                <div className="wl-card-body">
                  <div className="wl-card-top">
                    <h4
                      className="wl-card-name"
                      onClick={() => openProduct(product)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openProduct(product);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      title={name}
                    >
                      {name}
                    </h4>

                    <div className="wl-meta">
                      {brand && (
                        <span className="wl-meta-pill">
                          <TagIcon /> {brand}
                        </span>
                      )}

                      {shop && (
                        <span className="wl-meta-pill">
                          <StoreIcon /> {shop}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="wl-card-footer">
                    <div className="wl-price-block">
                      <span className="wl-price">
                        ${Number.isNaN(finalPrice) ? "0.00" : finalPrice.toFixed(2)}
                      </span>

                      {hasDiscount && (
                        <span className="wl-price-orig">
                          ${regularPrice.toFixed(2)}
                        </span>
                      )}
                    </div>

                    <div className="wl-actions">
                      <button
                        className="wl-remove-btn"
                        onClick={() => handleRemove(item.id)}
                        disabled={isRemoving}
                        title="Remove from wishlist"
                        type="button"
                      >
                        {isRemoving ? (
                          <Spinner size={12} color="#e05252" />
                        ) : (
                          <TrashIcon />
                        )}
                        <span>{isRemoving ? "Removing…" : "Remove"}</span>
                      </button>

                      <button
                        className="wl-cart-btn"
                        onClick={() => handleAddToCart(item)}
                        disabled={isAddingCart}
                        title="Add to cart"
                        type="button"
                      >
                        {isAddingCart ? <Spinner size={12} /> : <CartIcon />}
                        <span>{isAddingCart ? "Adding…" : "Add to Cart"}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}